import { toRefs, ref, computed, reactive, watch } from 'vue';
import { useEventListener } from '@zoomcatalog/composable';
import { useScreenWidth } from '@zoomcatalog/design-system';
import type { Page } from '@zoomcatalog/design-templates';
import { formatNewProductPricing } from '@zoomcatalog/design-templates';
import type { MaybeElement } from '@zoomcatalog/shared';
import { unwrapElement, makeInstanceFactory } from '@zoomcatalog/shared';
import { BasicPage, IPage, ISimplePage } from '@/service/ts-definitions';
import { settings } from '@/settings/DynamicSettings';
import { rootStore, useSelector } from '@/store/rootStore';

export type PagesMap = Record<string, Page>;

export type PaginationInstanceProps = {
  options: {
    initialPages: PagesMap;
    initialPageNumber: number;
    projectId: string;
    currency: string;
    limit: number;
    priceTableAttributes?: Record<string, boolean>;
  };
  handlers: {
    onPageChange?: (pageNumber: number) => void;
    onPageNotFound?: (pageNumber: number) => void;
    onSetPages?: (page: IPage) => void;
  };
};

export function normalizePage(
  pageData: IPage | ISimplePage | null | undefined,
  currency: string,
  priceTableAttributes?: Record<string, boolean>
): Page | null {
  if (!pageData) return null;
  const priceLevel =
    rootStore.state.auth.company?.slug === settings.SLUGS.SHOWDOWNDISPLAYS
      ? 'highest'
      : 'lowest';

  return {
    pageId: pageData.page_id ?? '',
    layoutId: pageData.layout_id ?? '',
    index: pageData.index,
    title: pageData.title,
    coverImage: pageData.cover_image,
    layout: pageData.layout,
    products: Object.values(pageData.products).reduce((products, product) => {
      if (!product) return {};

      const productData = { ...product, ...product?.data };

      return {
        ...products,
        [product.slot]: formatNewProductPricing(
          productData as any,
          currency,
          priceTableAttributes,
          priceLevel
        ),
      };
    }, {}),
    logos: pageData.logos,
    settings: {
      feature: {
        ...pageData.settings?.feature,
      },
      products: {
        ...pageData.settings?.products,
      },
      sections: {
        ...pageData.settings?.sections,
      },
      template: {
        ...pageData.settings?.template,
      },
    },
    templateName: pageData.layout.name,
  };
}

const [
  useMakePaginationInstance,
  usePaginationInstance,
  usePaginationHandlers,
] = makeInstanceFactory('pagination', (props: PaginationInstanceProps) => {
  const { options, handlers } = props;
  const { initialPages, initialPageNumber, projectId, limit } = toRefs(options);
  const loadedPagesIndexes = ref<Set<number>>(new Set());

  const screenWidth = useScreenWidth();
  // we use screen width instead of device type
  const screenType = computed(() =>
    screenWidth.value === 'sm' ? 'mobile' : 'desktop'
  );

  // element that will use scrollToTop handler
  const scrollRef = ref<Element | null>(null);
  const pages = ref<PagesMap>(initialPages.value);

  const pageNumber = ref(initialPageNumber.value); // the actual page number, not the array index
  const currentPages = computed(() => {
    const pageData = pages.value[pageNumber.value];
    const pageSettings = pageData?.settings;

    if (screenType.value === 'desktop' && pageSettings?.feature?.spreadPage) {
      return [
        pages.value[pageNumber.value],
        pages.value[
          pageSettings.feature.spreadPage === 'left'
            ? pageNumber.value + 1
            : pageNumber.value - 1
        ],
      ]
        .filter((maybePage): maybePage is Page => Boolean(maybePage))
        .sort((a, b) => a.index - b.index);
    }
    return [pageData].filter(Boolean);
  });
  const leftPage = computed(() => currentPages.value[0]);
  const rightPage = computed(() => currentPages.value[1]);
  const numberOfPages = computed(() => Object.keys(pages.value).length);
  const canGoNext = computed(
    () => pageNumber.value < totalPages.value && !isLastPage.value
  );
  const canGoPrevious = computed(
    () => (leftPage.value?.index ?? pageNumber.value) > 1
  );
  const isOnPaginationRange = computed(() =>
    isPageIndexOnPaginationRange(pageNumber.value)
  );
  const hasCurrentPages = computed(() => Boolean(currentPages.value.length));
  const isSpreadPage = computed(() => currentPages.value.length > 1);
  const isLastPage = computed(() => {
    if (isSpreadPage.value) {
      return rightPage.value.index === totalPages.value;
    }
    return pageNumber.value === totalPages.value;
  });
  const basicPagesList = useSelector((state) => state.project.data.pages);
  const basicPagesMap = computed(() =>
    basicPagesList.value.reduce<Record<string, BasicPage>>((pages, page) => {
      return {
        ...pages,
        [page.index]: { ...page },
      };
    }, {})
  );
  const totalPages = computed(() => basicPagesList.value.length);
  const currentBasicPages = computed(() => {
    const basicPageData = basicPagesMap.value[pageNumber.value];

    if (basicPageData?.spread_page) {
      return [
        basicPagesMap.value[pageNumber.value],
        basicPagesMap.value[
          basicPageData.spread_page === 'left'
            ? pageNumber.value + 1
            : pageNumber.value - 1
        ],
      ].sort((a, b) => a.index - b.index);
    }
    return [basicPagesMap.value[pageNumber.value]];
  });
  const pageRangeIndexesToFetch = computed(() => ({
    min: pageNumber.value - 5 < 0 ? 1 : pageNumber.value - 5,
    max:
      pageNumber.value + 5 > totalPages.value
        ? totalPages.value
        : pageNumber.value + 5,
  }));

  // handlers
  function setScrollRef(maybeElement: MaybeElement) {
    const element = unwrapElement(maybeElement);

    if (element) {
      scrollRef.value = element;
    }
  }

  function scrollToTop() {
    if (scrollRef.value) {
      scrollRef.value.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function goToNextPage() {
    if (canGoNext.value) {
      pageNumber.value = (rightPage.value?.index ?? pageNumber.value) + 1;
      handlers.onPageChange?.(pageNumber.value);
    }
  }

  function goToPreviousPage() {
    if (canGoPrevious.value) {
      pageNumber.value = (leftPage.value?.index ?? pageNumber.value) - 1;
      handlers.onPageChange?.(pageNumber.value);
    }
  }

  function goToPage(nextPageNumber: number) {
    if (pages.value[nextPageNumber]) {
      pageNumber.value = nextPageNumber;
    }
  }

  function pageNotFound() {
    pageNumber.value = 1;
    handlers.onPageNotFound?.(pageNumber.value);
  }

  function setPages(pagesData: IPage) {
    Object.assign(pages.value, { ...normalizePages(pagesData) });
    handlers.onSetPages?.(pagesData);
  }

  function normalizePages(page: IPage) {
    if (!page) return {};

    return {
      [page.index]: {
        ...page,
        ...normalizePage(
          page,
          props.options.currency,
          props.options.priceTableAttributes
        ),
      },
    };
  }

  function isPageIndexInBatchRange(pageIndex: number) {
    return (
      pageIndex >= pageRangeIndexesToFetch.value.min &&
      pageIndex <= pageRangeIndexesToFetch.value.max
    );
  }

  function isPageIndexOnPaginationRange(pageIndex: number) {
    return pageIndex >= 1 && pageIndex <= totalPages.value;
  }

  function getPageIdByPageIndex(pageIndex: number) {
    return basicPagesList.value.find(
      (basicPage) => basicPage.index === pageIndex
    )?.page_id;
  }

  useEventListener(window, 'keydown', (event) => {
    const keyCode = event.code.toLowerCase();

    switch (keyCode) {
      case 'arrowleft':
        goToPreviousPage();
        break;

      case 'arrowright':
        goToNextPage();
        break;

      default:
        break;
    }
  });

  watch(totalPages, () => {
    isOnPaginationRange.value
      ? handlers.onPageChange?.(pageNumber.value)
      : pageNotFound();
    scrollToTop();
  });

  return [
    reactive({
      pages,
      pageNumber,
      currentPages,
      numberOfPages,
      canGoNext,
      canGoPrevious,
      isOnPaginationRange,
      isSpreadPage,
      screenType,
      projectId,
      totalPages,
      limit,
      hasCurrentPages,
      isLastPage,
      basicPagesList,
      pageRangeIndexesToFetch,
      loadedPagesIndexes,
      currentBasicPages,
    }),
    {
      setScrollRef,
      scrollToTop,
      goToNextPage,
      goToPreviousPage,
      goToPage,
      setPages,
      normalizePages,
      isPageIndexOnPaginationRange,
      isPageIndexInBatchRange,
      getPageIdByPageIndex,
    },
  ];
});

export {
  useMakePaginationInstance,
  usePaginationInstance,
  usePaginationHandlers,
};
